/**
 * Product info tabs: one panel open at a time.
 *
 * Buttons carry role="tab" and aria-controls; clicking one shows its panel
 * and hides the others. Arrow keys move between tabs (the usual tablist
 * pattern), Home/End jump to the ends. With no script, every panel shows.
 *
 * A panel whose text runs past COLLAPSE_AFTER_LINES is cut to ~8 lines
 * (the cut itself is CSS, on .is-collapsible) and its "Read more" button is
 * shown to expand it in place. Short panels are left alone and the button
 * stays hidden. Each measured panel gets .is-measured, which lifts the
 * first-paint height hold in snippets/pdp-styles.liquid.
 */

/* Cut only when it saves something: text of 9 or 10 lines shows in full
   rather than hiding one or two lines behind a button. */
const COLLAPSE_AFTER_LINES = 10;

class PdpTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKey);
    window.addEventListener('load', this.#measureAll, { once: true });
    requestAnimationFrame(this.#measureAll);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKey);
    window.removeEventListener('load', this.#measureAll);
  }

  get #tabs() {
    return [...this.querySelectorAll('[role="tab"]')];
  }

  #select(tab) {
    for (const t of this.#tabs) {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      const panel = this.querySelector(`#${CSS.escape(t.getAttribute('aria-controls'))}`);
      if (!panel) continue;
      panel.hidden = !on;
      /* A hidden panel has no height to measure, so each one is sized the
         first time it is actually shown. */
      if (on) this.#measure(panel);
    }
  }

  #measureAll = () => {
    for (const panel of this.querySelectorAll('[role="tabpanel"]')) {
      if (!panel.hidden) this.#measure(panel);
    }
  };

  #measure(panel) {
    const body = panel.querySelector('.pdp-tabs__body');
    const more = panel.querySelector('.pdp-tabs__more');
    if (!body || !more) return;

    /* scrollHeight is the full text height even while the body is clipped,
       so this reads the same whether or not the panel is already cut. */
    const lineHeight = parseFloat(getComputedStyle(body).lineHeight) || 24;
    const long = body.scrollHeight > lineHeight * COLLAPSE_AFTER_LINES;

    panel.classList.toggle('is-collapsible', long);
    panel.classList.add('is-measured');
    more.hidden = !long;
    if (!long) panel.classList.remove('is-expanded');
    this.#syncButton(panel, more);
  }

  #syncButton(panel, more) {
    const expanded = panel.classList.contains('is-expanded');
    more.setAttribute('aria-expanded', String(expanded));
    const label = more.querySelector('.pdp-tabs__more-label');
    if (label) label.textContent = expanded ? 'Read less' : 'Read more';
  }

  #toggle(more) {
    const panel = more.closest('[role="tabpanel"]');
    if (!panel) return;
    const expanding = !panel.classList.contains('is-expanded');
    panel.classList.toggle('is-expanded', expanding);
    this.#syncButton(panel, more);

    /* Collapsing a long text can leave the reader far below where it now
       ends; bring the tabs back into view so they are not lost. */
    if (!expanding && this.getBoundingClientRect().top < 0) {
      this.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  #onClick = (event) => {
    const more = event.target.closest('.pdp-tabs__more');
    if (more && this.contains(more)) {
      this.#toggle(more);
      return;
    }
    const tab = event.target.closest('[role="tab"]');
    if (tab && this.contains(tab)) this.#select(tab);
  };

  #onKey = (event) => {
    const tabs = this.#tabs;
    const i = tabs.indexOf(document.activeElement);
    if (i < 0) return;
    let next = null;
    if (event.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
    else if (event.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
    else if (event.key === 'Home') next = tabs[0];
    else if (event.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;
    event.preventDefault();
    next.focus();
    this.#select(next);
  };
}

if (!customElements.get('pdp-tabs')) customElements.define('pdp-tabs', PdpTabs);
